import type { ReferenceStore } from '..';
import type { ReferenceLanguageSlice, ReferenceLanguageSliceState } from './types';
import type { StateCreator } from 'zustand';

import { actionLog } from '@core/store/util';

import {
  getAvailableLibraries,
  getDefaultLanguage,
  getDefaultLanguages,
  getDefaultLibraryForLanguage,
  isCustomSampleSelected,
} from './helpers';

export const initialState: ReferenceLanguageSliceState = {
  availableLanguageLibraries: [],
  availableLanguages: [],
  customCodeSamples: false,
  defaultLanguages: [],
  isCustomSampleSelected: false,
  languageLibrary: null,
  maxLanguages: 5,
  supportsSimpleMode: false,
  externalSnippetGenerator: undefined,
  useAllAvailableLanguages: true,
};

/**
 * Language state slice containing fields related to the language/library/request code sample picker
 */
export const createReferenceLanguageSlice: StateCreator<
  ReferenceLanguageSlice & ReferenceStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ReferenceLanguageSlice
> = (set, get) => ({
  language: {
    ...initialState,

    updateDefaultLanguages: defaultLanguages => {
      set(
        state => {
          state.language.defaultLanguages = defaultLanguages;
        },
        false,
        actionLog('updateDefaults', { defaultLanguages }),
      );
    },

    updateLanguage: (language, library = null) => {
      const availableLanguageLibraries = getAvailableLibraries(
        get().language.customCodeSamples,
        language,
        get().language.supportsSimpleMode,
        get().language.externalSnippetGenerator,
      );

      let languageLibrary = library;
      if (!languageLibrary) {
        // if there isn't a language library passed, use the default
        languageLibrary = getDefaultLibraryForLanguage(
          get().language.customCodeSamples,
          language,
          availableLanguageLibraries,
          get().language.supportsSimpleMode,
          get().language.externalSnippetGenerator,
        );
      }

      set(
        state => {
          state.language = {
            ...state.language,
            availableLanguageLibraries,
            isCustomSampleSelected: isCustomSampleSelected(get().language.customCodeSamples, language),
            language,
            languageLibrary,
          };
        },
        false,
        actionLog('updateLanguage', { language, library }),
      );
    },

    initialize: data => {
      const {
        availableLanguages,
        customCodeSamples,
        externalSnippetGenerator,
        language,
        maxLanguages,
        providedLanguages,
        supportsSimpleMode,
        useAllAvailableLanguages,
      } = data;
      set(
        state => {
          const defaultLanguages = getDefaultLanguages(availableLanguages, providedLanguages, maxLanguages);
          // language might be empty/undefined, so this function ensures it's populated with something
          const defaultLanguage = getDefaultLanguage(defaultLanguages, language, useAllAvailableLanguages);
          const availableLanguageLibraries = getAvailableLibraries(
            customCodeSamples,
            defaultLanguage,
            supportsSimpleMode,
            externalSnippetGenerator,
          );
          const languageLibrary = getDefaultLibraryForLanguage(
            customCodeSamples,
            defaultLanguage,
            availableLanguageLibraries,
            supportsSimpleMode,
            externalSnippetGenerator,
          );

          state.language = {
            ...state.language,
            availableLanguages,
            availableLanguageLibraries,
            customCodeSamples,
            defaultLanguages,
            externalSnippetGenerator,
            isCustomSampleSelected: isCustomSampleSelected(customCodeSamples, defaultLanguage),
            language: defaultLanguage,
            languageLibrary,
            maxLanguages,
            supportsSimpleMode,
            useAllAvailableLanguages,
          };
        },
        false,
        actionLog('initialize language slice', data),
      );
    },
  },
});
